import React from 'react'
import Tooltip, { TooltipProps } from '@mui/material/Tooltip'
import type { PopperProps } from '@mui/material/Popper'

interface CustomTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactElement
  offset?: [number, number]
  arrowXOffset?: number
  /**
   * Overrides the popper's anchor (e.g. a Popper virtual element for anchoring to a
   * line fragment). Routed through slotProps.popper — unlike MUI's deprecated
   * PopperProps prop, this survives the v7 upgrade.
   */
  popperAnchorEl?: PopperProps['anchorEl']
  /**
   * Flip against the trigger's clipping/scroll container instead of the viewport,
   * so the tooltip flips before sliding under overlaying siblings (e.g. the chat composer).
   */
  flipAltBoundary?: boolean
  /**
   * Replaces the default bubble classes entirely when set (MUI slot classNames don't
   * merge predictably against the defaults' !important utilities — replacement keeps
   * conflicting utilities out of the class list).
   */
  tooltipClassName?: string
  /** Replaces the default arrow classes entirely when set; same rationale as tooltipClassName. */
  arrowClassName?: string
  title: React.ReactNode
}

export const CustomizableTooltip = React.forwardRef<HTMLElement, CustomTooltipProps>(
  ({ children, title, offset = [0, -6], arrowXOffset, flipAltBoundary, popperAnchorEl, tooltipClassName, arrowClassName, ...props }, _ref) => {
    // Don't render tooltip if title is null or undefined
    if (!title) {
      return children
    }

    return (
      <Tooltip
        title={title}
        slotProps={{
          popper: {
            ...(popperAnchorEl ? { anchorEl: popperAnchorEl } : {}),
            modifiers: [
              { name: 'offset', options: { offset } },
              // The 16px viewport gutter. It must live here rather than as a margin
              // on the popper root: Popper positions with transforms and treats
              // margins as zero, so a margin shifts the tooltip (and its arrow) off
              // its anchor instead of creating a gutter.
              { name: 'preventOverflow', options: { padding: 16 } },
              ...(flipAltBoundary ? [{ name: 'flip', options: { altBoundary: true } }] : []),
            ],
          },
          tooltip: {
            className:
              tooltipClassName ??
              'flex items-center min-h-[40px] min-w-fit px-3 py-1.5 !bg-surface-popover !rounded-lg !text-text-secondary !font-sans border border-border-default shadow-[0_0.6px_25px_0_rgba(0,0,0,0.10),0_10px_10px_0_rgba(0,0,0,0.04)]',
          },
          arrow: {
            className: arrowClassName ?? '!text-surface-popover [&::before]:border [&::before]:border-border-default',
            sx: arrowXOffset ? { left: `${arrowXOffset}px !important` } : undefined,
          },
        }}
        {...props}
      >
        {children}
      </Tooltip>
    )
  }
)

export default CustomizableTooltip
